// A1.5 §2.1 — Career Totals as a full-width featured card. Replaces the
// previous All-Time Wins card AND the Career Totals collapsible section
// (its data was a superset of both, so duplication served no one).
//
// Sortable table — click any column header to re-sort. Default: total
// win% descending. Columns:
//   Rank · Owner · W · L · PF · PA · Win% · Playoff Apps · Titles
//
// Median-wins-aware: W/L/Win% read totalW/totalL/totalWinPct when present
// (matches the rest of the Almanac), falls through to H2H w/l otherwise.

import { useMemo, useState } from "react";
import theme from "../../data/theme";

const SORT_KEYS = {
  rank:        { label: "#",         align: "left",  accessor: (r) => r._rank },
  owner:       { label: "Owner",     align: "left",  accessor: (r) => r.owner.toLowerCase() },
  w:           { label: "W",         align: "right", accessor: (r) => r.totalW ?? r.w },
  l:           { label: "L",         align: "right", accessor: (r) => r.totalL ?? r.l },
  pf:          { label: "PF",        align: "right", accessor: (r) => r.pf },
  pa:          { label: "PA",        align: "right", accessor: (r) => r.pa },
  winPct:      { label: "Win%",      align: "right", accessor: (r) => r.totalWinPct ?? r.winPct },
  playoffApps: { label: "Playoffs",  align: "right", accessor: (r) => r.playoffApps || 0 },
  titles:      { label: "Titles",    align: "right", accessor: (r) => r.titles || 0 },
};
const SORT_ORDER = ["rank", "owner", "w", "l", "pf", "pa", "winPct", "playoffApps", "titles"];
const DEFAULT_SORT = { key: "w", dir: "desc" };

export default function CareerTotalsCard({ rows }) {
  const [sort, setSort] = useState(DEFAULT_SORT);
  // Stable default rank (by W desc — careerTotals already sorted by totalW
  // upstream in funStats). User-toggled sorts re-sort but preserve _rank
  // for the # column to show original standing.
  const rankedRows = useMemo(() => rows.map((r, i) => ({ ...r, _rank: i + 1 })), [rows]);
  // A1.6 §2 — split current owners from former owners using the per-row
  // `core` flag (set in funStats from `games >= minCoreGames`). Sort within
  // each group so the divider stays visible regardless of which column the
  // user clicks. Keeps history intact — no rows are dropped.
  const { currentRows, formerRows } = useMemo(() => {
    const acc = SORT_KEYS[sort.key]?.accessor || (() => 0);
    const dir = sort.dir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      const av = acc(a), bv = acc(b);
      if (av === bv) return 0;
      if (typeof av === "string") return av < bv ? -dir : dir;
      return (av - bv) * dir;
    };
    const current = rankedRows.filter(r => r.core !== false).sort(cmp);
    const former = rankedRows.filter(r => r.core === false).sort(cmp);
    return { currentRows: current, formerRows: former };
  }, [rankedRows, sort]);

  if (!rows?.length) return null;

  const onHeaderClick = (key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      // Default direction: desc for numerics, asc for strings.
      const isString = typeof SORT_KEYS[key]?.accessor({ owner: "x" }) === "string";
      return { key, dir: isString ? "asc" : "desc" };
    });
  };

  return (
    <section className="almanac-career-card">
      <div className="almanac-card-eyebrow">Career Totals · {rows.length} owners</div>
      <div className="almanac-career-tablewrap">
        <table className="almanac-career-table" style={{ fontFamily: theme.fonts.body }}>
          <thead>
            <tr>
              {SORT_ORDER.map((key) => {
                const col = SORT_KEYS[key];
                const isActive = sort.key === key;
                return (
                  <th
                    key={key}
                    onClick={() => onHeaderClick(key)}
                    style={{
                      textAlign: col.align,
                      fontFamily: theme.fonts.almanac,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    aria-sort={isActive ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    {col.label}{isActive ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((r) => renderRow(r))}
            {formerRows.length > 0 && (
              <>
                <tr aria-hidden="true">
                  <td
                    colSpan={SORT_ORDER.length}
                    style={{
                      paddingTop: 14, paddingBottom: 6,
                      fontFamily: theme.fonts.almanac,
                      fontSize: 11, letterSpacing: 1.8,
                      textTransform: "uppercase",
                      color: theme.colors.text.muted,
                      borderTop: `1px solid ${theme.colors.border?.subtle || "rgba(255,255,255,0.08)"}`,
                    }}
                  >
                    Former Owners
                  </td>
                </tr>
                {formerRows.map((r) => renderRow(r, { faded: true }))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// A1.6 §2 — shared row renderer for current/former groups. Former owners get
// a subtle opacity so the divider's purpose reads at a glance.
function renderRow(r, { faded = false } = {}) {
  const baseOpacity = faded ? 0.62 : 1;
  return (
    <tr key={r.owner} style={{ opacity: baseOpacity }}>
      <td style={{ textAlign: "left", color: theme.colors.text.muted }}>{r._rank}</td>
      <td style={{ textAlign: "left", fontWeight: 600 }}>{r.owner}</td>
      <td style={{ textAlign: "right" }}>{r.totalW ?? r.w}</td>
      <td style={{ textAlign: "right" }}>{r.totalL ?? r.l}</td>
      <td style={{ textAlign: "right" }}>{Math.round(r.pf || 0)}</td>
      <td style={{ textAlign: "right" }}>{Math.round(r.pa || 0)}</td>
      <td style={{ textAlign: "right", fontWeight: 600 }}>
        {((r.totalWinPct ?? r.winPct ?? 0) * 100).toFixed(1)}%
      </td>
      <td style={{ textAlign: "right" }}>{r.playoffApps || 0}</td>
      <td style={{ textAlign: "right", color: r.titles ? "#fb923c" : theme.colors.text.muted, fontWeight: 700 }}>
        {r.titles || 0}
      </td>
    </tr>
  );
}
